"use client"

import type { ReactNode } from "react"
import OverviewStats from "./overview-stats"
import ContentChart from "./content-chart"
import RecentPosts from "./recent-posts"
import PopularArticles from "./popular-articles"
import ContentCategories from "./content-categories"
import UserActivity from "./user-activity"
import SystemHistory from "./system-history"
import LiveTicker from "./live-ticker"
import DashboardHeader from "./dashboard-header"

function SectionDivider({ eyebrow, title, hint }: { eyebrow: string; title: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/40 pb-2.5">
      <div className="flex items-baseline gap-3">
        <span className="font-display text-base italic text-[var(--brand-amber)] tabular-nums">{eyebrow}</span>
        <span className="text-border">/</span>
        <h2 className="text-sm font-medium text-foreground">{title}</h2>
      </div>
      {hint && <span className="font-mono text-[11px] text-muted-foreground">{hint}</span>}
    </div>
  )
}

function Stagger({ delay = 0, className = "", children }: { delay?: number; className?: string; children: ReactNode }) {
  return (
    <div
      className={`animate-stagger-in ${className}`}
      style={{ ["--stagger-delay" as string]: `${delay}ms` }}
    >
      {children}
    </div>
  )
}

export default function CMSDashboardContent() {
  return (
    <div className="w-full min-w-0 space-y-12 sm:space-y-14">
      <DashboardHeader />

      {/* Live ticker */}
      <Stagger delay={80}>
        <LiveTicker />
      </Stagger>

      {/* Hero KPIs */}
      <Stagger delay={140}>
        <OverviewStats />
      </Stagger>

      {/* Live operation */}
      <section className="space-y-5">
        <SectionDivider eyebrow="01" title="Operação ao vivo" hint="updates contínuos" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-6">
          <Stagger delay={220} className="h-full">
            <SystemHistory />
          </Stagger>
          <Stagger delay={290} className="h-full">
            <UserActivity />
          </Stagger>
        </div>
      </section>

      {/* Analysis */}
      <section className="space-y-5">
        <SectionDivider eyebrow="02" title="Análise de captura" hint="últimos 14 dias" />
        <Stagger delay={360}>
          <ContentChart />
        </Stagger>
      </section>

      {/* Discoveries */}
      <section className="space-y-5">
        <SectionDivider eyebrow="03" title="Descobertas recentes" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 lg:gap-6">
          <Stagger delay={500} className="lg:col-span-2">
            <RecentPosts />
          </Stagger>
          <Stagger delay={570}>
            <ContentCategories />
          </Stagger>
        </div>
        <Stagger delay={640}>
          <PopularArticles />
        </Stagger>
      </section>
    </div>
  )
}
